import { type ReactNode, useEffect } from 'react';
import { useShallow } from 'zustand/shallow';

import { useFrustumViewerSyncHandler } from 'app/modules/wallforming/components/frustum-viewer/hooks/useFrustumViewerSyncHandler';
import { useMainSceneEventHandler } from 'app/modules/wallforming/components/scenes/hooks/useMainSceneEventHandler';
import { useRaycastPlane } from 'app/modules/wallforming/components/scenes/objects/helpers/hooks/useRaycastPlane';
import { useSelectionHelper } from 'app/modules/wallforming/components/scenes/objects/helpers/hooks/useSelectionHelper';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import {
    acquireArticleBatchWorker,
    releaseArticleBatchWorker
} from 'app/modules/wallforming/components/workers/articleBatchWorkerSingleton';
import {
    acquireDraggingBoxWorker,
    releaseDraggingBoxWorker
} from 'app/modules/wallforming/components/workers/draggingBoxWorkerSingleton';

import 'app/modules/wallforming/custom-styles/customBoxSelectionStyle.css';

interface MainSceneProps {
    children: ReactNode;
}

export const MainScene = ({ children }: MainSceneProps) => {
    const { handleBoxSelectionStart, handleBoxSelection, handleBoxSelectionEnd, isSelectionHelperEnabled } =
        usePlacementStore(
            useShallow((state) => ({
                handleBoxSelectionStart: state.handleBoxSelectionStart,
                handleBoxSelection: state.handleBoxSelection,
                handleBoxSelectionEnd: state.handleBoxSelectionEnd,
                isSelectionHelperEnabled: state.isSelectionHelperEnabled
            }))
        );

    useSelectionHelper({
        cssClassName: 'selection-box',
        isEnabled: isSelectionHelperEnabled,
        onSelectStart: handleBoxSelectionStart,
        onSelectMove: handleBoxSelection,
        onSelectOver: handleBoxSelectionEnd
    });

    useRaycastPlane();
    useFrustumViewerSyncHandler();
    useMainSceneEventHandler();

    useEffect(() => {
        acquireArticleBatchWorker();
        acquireDraggingBoxWorker();

        return () => {
            releaseArticleBatchWorker();
            releaseDraggingBoxWorker();
        };
    }, []);

    return <group>{children}</group>;
};
